import React from "react";
import { Link } from "react-router-dom";
import EstatePage from "@/components/sites/EstatePage";
import Reveal from "@/components/sites/Reveal";
import { useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { lbl } from "@/lib/terms";
import { withPreview } from "@/lib/sitePreview";

/**
 * Selling and letting, in the Estate theme.
 *
 * One component for both, because they are the same page with a different
 * block of the agency's record behind them: `brand.selling` and
 * `brand.letting` have identical shapes (headline, sub, intro, why_us,
 * fee_options, guarantee) and the classic pages were already near-copies
 * of each other. Two files would be two places for one of them to fall
 * behind.
 *
 * Every word is the agency's. The classic version invents a headline when
 * the record has none — "Sell your home with a local team who actually
 * know your street" — which is a claim about a business nobody here has
 * met. Here an absent block means an absent section.
 */
export default function EstateSellLet({ kind = "sell", preview = true }) {
  const brand = useBrand();
  const market = useMarket();
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const letting = kind === "let";
  const block = (letting ? brand.letting : brand.selling) || {};
  const fees = block.fee_options || [];
  const reasons = block.why_us || [];
  const p = (href) => withPreview(href, preview);

  const what = letting ? "Letting" : "Selling";
  const heading = block.headline
    || `${what} with ${biz.name || "us"}`;

  return (
    <EstatePage
      preview={preview}
      title={biz.name
        ? `${letting ? "Let Your Property" : "Sell Your Home"}, ${biz.name}`
          + (brand.regions_covered_prose ? ` · ${brand.regions_covered_prose}` : "")
        : undefined}
      description={block.intro || block.sub || undefined}
      eyebrow={`${what} with ${biz.short_name || biz.name || "us"}`}
      heading={heading}
      lede={block.sub || ""}
      crumbs={[{ label: letting ? "Let" : "Sell" }]}
    >
      {block.intro && (
        <Reveal>
          <p className="lede" style={{ maxWidth: "calc(62 * var(--ch-body))" }}>{block.intro}</p>
        </Reveal>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
        <Link className="btn" to={p(tenantPath("valuation"))}>
          {lbl(letting ? "free_valuation_cta" : "sell_valuation_cta", market)}
        </Link>
        <Link className="btn ghost" to={p(tenantPath("contact"))}>Talk to us first</Link>
      </div>

      {reasons.length > 0 && (
        <Reveal>
          <div className="section-head" style={{ marginTop: 64 }}>
            <div>
              <h2 className="display-s">Why {letting ? "let" : "sell"} with us</h2>
            </div>
          </div>
          <ul className="reasons">
            {reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        </Reveal>
      )}

      {fees.length > 0 && (
        <Reveal>
          <div className="section-head" style={{ marginTop: 64 }}>
            <div>
              <div className="eyebrow">Our fees, your decision</div>
              <h2 className="display-s" style={{ marginTop: 10 }}>What it costs</h2>
            </div>
          </div>
          {block.guarantee && (
            <p className="note" style={{ maxWidth: "calc(58 * var(--ch-body))", marginBottom: 20 }}>{block.guarantee}</p>
          )}
          {/* data-cols, not an inline style: an inline style beats the
              phone media query, and a two-option agency got two columns
              at 375px with the price painting off the screen. */}
          <div className="fees" data-cols={fees.length < 3 ? fees.length : undefined}>
            {fees.map((fee) => (
              <div key={fee.name} className={`fee${fee.featured ? " lead" : ""}`}>
                {fee.featured && <div className="tagline">Most chosen</div>}
                <h3>{fee.name}</h3>
                {fee.price_label && <div className="amount">{fee.price_label}</div>}
                {fee.detail && <p className="detail">{fee.detail}</p>}
                {fee.includes?.length > 0 && (
                  <ul>{fee.includes.map((inc) => <li key={inc}>{inc}</li>)}</ul>
                )}
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {block.promo && (
        <Reveal>
          <p className="warn" style={{ marginTop: 48, fontSize: 15 }}>{block.promo}</p>
        </Reveal>
      )}
    </EstatePage>
  );
}
